import { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from '@legendapp/state/react';
import { Command } from 'cmdk';
import { Trash2 } from 'lucide-react';
import { useRAGChat } from '../../context/RAGChatContext';
import { useAutoScrollToBottom } from '../../hooks/useAutoScrollToBottom';
import { useStreamingAutoScroll } from '../../hooks/useStreamingAutoScroll';
import SearchResults from '../search/SearchResults';
import { AIChatEmptyState, createEmptyStates } from './AIChatMessages';
import RAGMessageList from './RAGMessageList';
import RAGInputArea from './RAGInputArea';
import SearchModeToggle from './SearchModeToggle';
import { useSearchModes } from './useSearchModes';
import './LibrarySearchTab.css';
import { ICON_SIZE } from '../../design-system';
import { isImeComposing } from '../../utils/imeUtils';
import {
  getPanelSearchScopeKey,
  panelSearch$,
  setPanelSearchKeyword,
} from '../../store/panelSearch$';

/**
 * The library panel's single search surface: keyword search and RAG chat behind
 * one input, switched from the header. Keyword mode reuses the same global
 * index and result component as Cmd+K, so the two surfaces cannot drift.
 */
export function LibrarySearchTab({ onDismiss = () => {} }) {
  const { t } = useTranslation(['reader', 'articles', 'common', 'search']);
  const navigate = useNavigate();
  const emptyStates = createEmptyStates(t);
  const ragContext = useRAGChat();

  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Keyword state is session-scoped per scope (All / Inbox / one list), so it
  // survives panel close/reopen and tab switches, never crosses scopes, and
  // never reaches storage. The AI draft lives separately in the same entry.
  const scopeKey = ragContext
    ? getPanelSearchScopeKey({ scopeType: ragContext.scopeType, scopeId: ragContext.scopeId })
    : 'all';
  const panelSearchQuery = useSelector(() => panelSearch$[scopeKey]?.keyword.get() ?? '');

  const {
    mode, isKeyword, input, setInput,
    changeMode, changeInput, commitKeyword, clearKeyword,
  } = useSearchModes({
    scopeKey,
    keyword: panelSearchQuery,
    setKeyword: (keyword) => setPanelSearchKeyword(scopeKey, keyword),
    inputRef,
    debounceMs: 150,
  });

  const {
    scopeDisplayName,
    messages,
    loading,
    sending,
    error,
    sendMessage,
    clearChat,
    deleteMessage,
    isOnline,
    searchingArticles,
    streamingState,
    isStreamingActive,
    forceCompleteStreaming,
    handleAnimationComplete,
    stopStreaming,
    streamBufferRef,
    visibleLengthRef,
    streamingMessageIndex,
  } = ragContext || {};

  const messagesEndRef = useAutoScrollToBottom([messages]);
  const { onContentUpdate: streamingScrollUpdate } = useStreamingAutoScroll({
    containerRef: messagesContainerRef,
    isStreaming: isStreamingActive,
  });

  const handleSend = () => {
    if (isKeyword) {
      commitKeyword();
      return;
    }
    if (!input.trim() || sending || !isOnline) return;
    sendMessage(input.trim());
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (isImeComposing(e)) return;
    if (isKeyword && e.key === 'Escape' && input) {
      e.preventDefault();
      clearKeyword();
      return;
    }
    // Cmdk owns Enter and arrow-key selection while keyword results are shown.
    if (isKeyword) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCitationClick = useCallback((number, source) => {
    if (source?.article_id) navigate(`/article/${source.article_id}`);
  }, [navigate]);

  // The transcript contract starts at RAGChatProvider. If the route-scoped
  // provider is unavailable, do not pass a partial context into its children.
  if (!ragContext) return null;

  // Global keyword search is entirely local; a slow RAG bootstrap must not
  // block it behind the AI loading state.
  if (loading && !isKeyword) {
    return <div className="ai-chat-loading">{t('common:loading')}</div>;
  }

  // The shell keeps this tab mounted while the RAG context is still resolving its
  // scope, so every scope-interpolated string needs a stand-in until it lands.
  const scopeLabel = scopeDisplayName || t('articles:dashboard.allArticles');

  const inputArea = (
    <RAGInputArea
      ref={inputRef}
      value={input}
      onChange={changeInput}
      onSend={handleSend}
      onKeyDown={handleKeyDown}
      disabled={!isKeyword && (!isOnline || sending)}
      placeholder={isKeyword
        ? t('search:placeholder')
        : t('reader:ragChat.searchPlaceholder', { scope: scopeLabel.toLowerCase() })}
      isStreamingActive={!isKeyword && isStreamingActive}
      onStop={stopStreaming}
      showFilters={!isKeyword}
      compactFilters
      mergeSettings
      isOnline={isKeyword || isOnline}
      filtersLeading={<SearchModeToggle mode={mode} onModeChange={changeMode} />}
      filtersTrailing={(
        <>
          {!isKeyword && messages?.length > 0 && (
            <button
              type="button"
              className="input-area-filter input-area-icon-filter"
              onClick={clearChat}
              aria-label={t('reader:aiChat.clearChat')}
              title={t('reader:aiChat.clearChat')}
            >
              <Trash2 size={ICON_SIZE.xs} aria-hidden="true" />
            </button>
          )}
        </>
      )}
    />
  );

  if (isKeyword) {
    return (
      <Command
        label={t('common:search')}
        shouldFilter={false}
        loop
        className="library-keyword-search"
      >
        <SearchResults
          debouncedQuery={panelSearchQuery}
          onDismiss={onDismiss}
          showActions={false}
        />
        {inputArea}
      </Command>
    );
  }

  return (
    <>
      <RAGMessageList
        messages={messages}
        emptyState={<AIChatEmptyState {...emptyStates.rag(scopeLabel)} />}
        suggestionContext="global_chat"
        onDelete={deleteMessage}
        onSuggestionClick={sendMessage}
        sending={sending}
        searchingArticles={searchingArticles}
        error={error}
        onCitationClick={handleCitationClick}
        streamingState={streamingState}
        streamingMessageIndex={streamingMessageIndex}
        forceCompleteStreaming={forceCompleteStreaming}
        streamBufferRef={streamBufferRef}
        visibleLengthRef={visibleLengthRef}
        handleAnimationComplete={handleAnimationComplete}
        onStreamingContentUpdate={streamingScrollUpdate}
        messagesEndRef={messagesEndRef}
        containerRef={messagesContainerRef}
      />
      {inputArea}
    </>
  );
}

export default LibrarySearchTab;
